import React, { useMemo } from 'react';
import { Button, Skeleton, Alert, Tag, Empty } from 'antd';
import {
  ShopOutlined,
  AppstoreOutlined,
  MessageOutlined,
  StarOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  CalendarOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import PageShell from '../components/Layout/PageShell';
import { useAppSelector } from '../hooks/redux';
import type { Lead, Listing, Review } from '../types';

dayjs.extend(relativeTime);

function greetingForNow(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function formatNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function leadListingTitle(lead: Lead): string {
  const l = lead.listing;
  if (l && typeof l === 'object' && 'title' in l) {
    const t = (l as { title?: string }).title;
    if (t) return t;
  }
  return 'New enquiry';
}

function leadStoreLabel(lead: Lead): string {
  const s = lead.store;
  if (s && typeof s === 'object' && 'storeName' in s) {
    return (s as { storeName?: string }).storeName || 'Store';
  }
  return 'Store';
}

function listingStoreLabel(listing: Listing): string {
  const s = listing.store;
  if (s && typeof s === 'object' && 'storeName' in s) {
    return (s as { storeName?: string }).storeName || '';
  }
  return '';
}

function reviewAuthor(review: Review): string {
  const u = review.user;
  if (u && typeof u === 'object' && 'profile' in u) {
    const p = (u as { profile?: { firstName?: string; lastName?: string } }).profile;
    if (p) {
      const name = [p.firstName, p.lastName].filter(Boolean).join(' ');
      if (name) return name;
    }
  }
  return 'Customer';
}

type FeedItem = {
  id: string;
  kind: 'lead' | 'listing' | 'review';
  title: string;
  meta: string;
  when: string;
  href: string;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { stats, recentActivities, leadTrends, loading, error } = useAppSelector(
    (state) => state.dashboard
  );

  const firstName = user?.profile?.firstName?.trim() || 'there';

  const chartData = useMemo(() => {
    if (!leadTrends?.length) return [];
    const pad = (n: number) => String(n).padStart(2, '0');
    return [...leadTrends]
      .map((t) => {
        const d = new Date(t._id.year, t._id.month - 1, t._id.day);
        return {
          sortKey: `${t._id.year}-${pad(t._id.month)}-${pad(t._id.day)}`,
          label: dayjs(d).format('MMM D'),
          leads: t.count,
        };
      })
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  }, [leadTrends]);

  const leadSumOnChart = useMemo(() => chartData.reduce((s, d) => s + d.leads, 0), [chartData]);

  const activityFeed = useMemo((): FeedItem[] => {
    if (!recentActivities) return [];
    const items: FeedItem[] = [];

    recentActivities.leads?.forEach((lead) => {
      items.push({
        id: `lead-${lead._id}`,
        kind: 'lead',
        title: leadListingTitle(lead),
        meta: leadStoreLabel(lead),
        when: lead.createdAt,
        href: `/leads/${lead._id}`,
      });
    });

    recentActivities.listings?.forEach((listing) => {
      const store = listingStoreLabel(listing);
      items.push({
        id: `listing-${listing._id}`,
        kind: 'listing',
        title: listing.title || 'Listing',
        meta: store || 'Listing published',
        when: listing.createdAt,
        href: `/listings/${listing._id}/edit`,
      });
    });

    recentActivities.reviews?.forEach((review) => {
      items.push({
        id: `review-${review._id}`,
        kind: 'review',
        title: `${reviewAuthor(review)} · ${review.rating}★`,
        meta: review.comment?.slice(0, 72) || 'New review',
        when: review.createdAt,
        href: '/reviews',
      });
    });

    return items.sort((a, b) => dayjs(b.when).valueOf() - dayjs(a.when).valueOf()).slice(0, 8);
  }, [recentActivities]);

  const listingActivePct =
    stats && stats.listings.total > 0
      ? Math.round((stats.listings.active / stats.listings.total) * 100)
      : 0;

  const storeActivePct =
    stats && stats.stores.total > 0
      ? Math.round((stats.stores.active / stats.stores.total) * 100)
      : 0;

  const avgRating = stats ? Number(stats.reviews.averageRating || 0) : 0;
  const hasReviews = !!stats && stats.reviews.total > 0;

  const mainKpis = [
    {
      key: 'stores',
      label: 'Stores',
      value: formatNum(stats?.stores.total ?? 0),
      hint:
        stats && stats.stores.total > 0
          ? `${formatNum(stats.stores.active)} open · ${formatNum(stats.stores.pending)} closed`
          : 'Add your shop to get started',
      icon: <ShopOutlined className="text-lg sm:text-xl" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-[#0171BF] to-[#015a9e]',
      onClick: () => navigate('/stores'),
      linkLabel: 'View my stores',
    },
    {
      key: 'listings',
      label: 'Listings',
      value: formatNum(stats?.listings.total ?? 0),
      hint:
        stats && stats.listings.total > 0
          ? `${formatNum(stats.listings.active)} live · ${formatNum(stats.listings.pending)} not live`
          : 'Post items buyers can see',
      icon: <AppstoreOutlined className="text-lg sm:text-xl" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-emerald-500 to-emerald-700',
      onClick: () => navigate('/listings'),
      linkLabel: 'View my listings',
    },
    {
      key: 'leads',
      label: 'Leads',
      value: formatNum(stats?.leads.total ?? 0),
      hint:
        stats && (stats.leads.new ?? 0) > 0
          ? `${formatNum(stats.leads.new)} new — open Leads to reply`
          : 'Messages from interested buyers',
      icon: <MessageOutlined className="text-lg sm:text-xl" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-[#F7941F] to-[#d97d0f]',
      onClick: () => navigate('/leads'),
      linkLabel: 'View my leads',
    },
    {
      key: 'rating',
      label: 'Rating',
      value: hasReviews ? avgRating.toFixed(1) : '—',
      hint: hasReviews
        ? `${formatNum(stats!.reviews.total)} reviews · out of 5 stars`
        : 'Shows up when buyers leave reviews',
      icon: <StarOutlined className="text-lg sm:text-xl" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-rose-500 to-rose-700',
      onClick: () => navigate('/reviews'),
      linkLabel: 'View reviews',
    },
  ];

  const pulseStats = [
    {
      label: 'New leads',
      value: formatNum(stats?.leads.new ?? 0),
      sub: 'Not replied yet',
      icon: <ThunderboltOutlined className="text-[#F7941F]" aria-hidden />,
      action: () => navigate('/leads'),
    },
    {
      label: 'Today',
      value: formatNum(stats?.leads.today ?? 0),
      sub: 'So far today',
      icon: <CalendarOutlined className="text-[#0171BF]" aria-hidden />,
      action: () => navigate('/leads'),
    },
    {
      label: 'This week',
      value: formatNum(stats?.leads.thisWeek ?? 0),
      sub: 'Last 7 days',
      icon: <RiseOutlined className="text-emerald-600" aria-hidden />,
      action: () => navigate('/leads'),
    },
    {
      label: 'Store views',
      value: formatNum(stats?.views.total ?? 0),
      sub: 'How often your shop was seen',
      icon: <EyeOutlined className="text-violet-600" aria-hidden />,
      action: () => navigate('/analytics'),
    },
  ];

  const quickActions = [
    {
      title: 'Create store',
      description: 'Set up a new shop page',
      icon: <ShopOutlined className="text-lg" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-[#0171BF] to-[#015a9e]',
      onClick: () => navigate('/stores/create'),
    },
    {
      title: 'Add listing',
      description: 'Add something to sell',
      icon: <AppstoreOutlined className="text-lg" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-emerald-500 to-emerald-700',
      onClick: () => navigate('/listings/create'),
    },
    {
      title: 'View reports',
      description: 'See more numbers and charts',
      icon: <BarChartOutlined className="text-lg" aria-hidden />,
      iconClass: 'bg-gradient-to-br from-[#F7941F] to-[#d97d0f]',
      onClick: () => navigate('/analytics'),
    },
  ];

  const showSkeleton = loading || !stats;

  const headerChips = (
    <>
      <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm">
        {dayjs().format('dddd, MMMM D')}
      </span>
      {!showSkeleton && stats && stats.leads.new > 0 ? (
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900">
          You have {formatNum(stats.leads.new)} new {stats.leads.new === 1 ? 'lead' : 'leads'}
        </span>
      ) : null}
    </>
  );

  return (
    <PageShell
      title={`${greetingForNow()}, ${firstName}`}
      description="Here is a simple summary of your shop, your items for sale, and messages from buyers."
      headerExtra={headerChips}
      className="dashboard-shell"
    >
      <div className="dashboard-surface space-y-5 sm:space-y-6">
        {error ? (
          <Alert
            type="warning"
            showIcon
            message="We could not refresh this page"
            description={error}
            className="rounded-2xl border-amber-200 bg-amber-50/90"
          />
        ) : null}

        {/* Main numbers */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 xl:grid-cols-4">
          {showSkeleton
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <Skeleton active paragraph={{ rows: 2 }} className="p-0" />
                </div>
              ))
            : mainKpis.map((kpi) => (
                <button
                  key={kpi.key}
                  type="button"
                  onClick={kpi.onClick}
                  className="group flex h-full w-full cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm outline-none ring-[#0171BF]/30 transition hover:border-[#0171BF]/40 hover:shadow-md focus-visible:ring-2 sm:p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {kpi.label}
                      </p>
                      <p className="mt-1 break-words text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                        {kpi.value}
                      </p>
                      <p className="mt-2 text-sm leading-snug text-slate-600">{kpi.hint}</p>
                    </div>
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-md sm:h-14 sm:w-14 ${kpi.iconClass}`}
                    >
                      {kpi.icon}
                    </div>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#0171BF] underline-offset-2 group-hover:underline">
                    {kpi.linkLabel}
                    <ArrowRightOutlined className="text-xs" />
                  </span>
                </button>
              ))}
        </div>

        {!showSkeleton && stats ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 lg:items-stretch">
            <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:col-span-8 xl:grid-cols-4">
              {pulseStats.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={p.action}
                  className="flex min-h-[5.5rem] cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white px-3 py-3 text-left shadow-sm transition hover:border-[#0171BF]/35 hover:shadow-md sm:min-h-0 sm:px-4 sm:py-4"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      {p.label}
                    </span>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-50 sm:h-9 sm:w-9">
                      {p.icon}
                    </span>
                  </div>
                  <p className="mt-2 text-xl font-semibold tabular-nums text-slate-900 sm:text-2xl">
                    {p.value}
                  </p>
                  <p className="text-xs text-slate-500 sm:text-sm">{p.sub}</p>
                </button>
              ))}
            </div>
            <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:col-span-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Store & listings
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    Open stores and live listings help buyers find you. You can manage both from your shop page.
                  </p>
                </div>
                <Button
                  type="primary"
                  className="h-10 shrink-0 cursor-pointer rounded-xl bg-[#0171BF] px-4"
                  onClick={() => navigate('/stores')}
                >
                  Manage stores
                </Button>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="mb-1 flex justify-between text-xs text-slate-600">
                    <span>Stores open</span>
                    <span className="tabular-nums font-medium text-slate-900">
                      {formatNum(stats.stores.active)} of {formatNum(stats.stores.total)}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-[#0171BF]"
                      style={{ width: `${Math.min(100, storeActivePct)}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex justify-between text-xs text-slate-600">
                    <span>Listings live</span>
                    <span className="tabular-nums font-medium text-slate-900">
                      {formatNum(stats.listings.active)} of {formatNum(stats.listings.total)}
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-emerald-500"
                      style={{ width: `${Math.min(100, listingActivePct)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:gap-5 lg:grid-cols-12 lg:items-start">
          <div className="flex flex-col gap-4 sm:gap-5 lg:col-span-8">
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:px-5">
                <div>
                  <h2 className="text-base font-semibold text-slate-900 sm:text-lg">Leads over time</h2>
                  <p className="text-sm text-slate-500">
                    {chartData.length > 0
                      ? `Each day on the chart is how many leads you got that day. Total on this chart: ${formatNum(leadSumOnChart)}.`
                      : 'When people contact you, you will see a simple chart here.'}
                  </p>
                </div>
                <Button
                  type="link"
                  className="m-0 cursor-pointer !p-0 font-medium text-[#0171BF]"
                  onClick={() => navigate('/analytics')}
                >
                  Open reports
                </Button>
              </div>
              <div className="px-3 pb-4 pt-2 sm:px-5 sm:pb-5">
                {showSkeleton ? (
                  <Skeleton.Node active className="!h-56 !w-full min-h-[14rem] rounded-xl" />
                ) : chartData.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center sm:py-10">
                    <p className="mx-auto max-w-md text-sm text-slate-600">
                      No lead history yet. Add a listing and keep your store open so buyers can message you — then this
                      chart will fill in.
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      <Button type="primary" className="rounded-lg bg-[#0171BF]" onClick={() => navigate('/listings/create')}>
                        Add a listing
                      </Button>
                      <Button className="rounded-lg" onClick={() => navigate('/listings')}>
                        My listings
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="h-56 w-full min-w-0 sm:h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData} margin={{ top: 10, right: 8, left: -20, bottom: 4 }}>
                        <defs>
                          <linearGradient id="leadArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#0171BF" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#0171BF" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fill: '#64748b', fontSize: 10 }}
                          tickLine={false}
                          axisLine={{ stroke: '#e2e8f0' }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tick={{ fill: '#64748b', fontSize: 10 }}
                          tickLine={false}
                          axisLine={false}
                          width={28}
                          allowDecimals={false}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                          }}
                          labelStyle={{ color: '#0f172a', fontWeight: 600 }}
                          formatter={(v: number | string) => [`${v}`, 'Leads']}
                        />
                        <Area
                          type="monotone"
                          dataKey="leads"
                          stroke="#0171BF"
                          strokeWidth={2}
                          fill="url(#leadArea)"
                          dot={false}
                          activeDot={{ r: 4, strokeWidth: 0, fill: '#0171BF' }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </section>

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-4 sm:px-5">
                <div>
                  <h2 className="text-base font-semibold text-slate-900 sm:text-lg">Recent activity</h2>
                  <p className="text-sm text-slate-500">Newest items first</p>
                </div>
                <Button
                  type="link"
                  className="m-0 cursor-pointer !p-0 font-medium text-[#0171BF]"
                  onClick={() => navigate('/leads')}
                >
                  All leads
                </Button>
              </div>
              <div className="px-2 pb-2 pt-1 sm:px-4 sm:pb-4">
                {showSkeleton ? (
                  <Skeleton active paragraph={{ rows: 5 }} className="px-2 py-2" />
                ) : activityFeed.length === 0 ? (
                  <div className="py-10 text-center">
                    <Empty
                      description={
                        <span className="text-slate-600">
                          Nothing new yet. When you get leads or post listings, they will show up here.
                        </span>
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {activityFeed.map((item) => {
                      const tag =
                        item.kind === 'lead' ? (
                          <Tag className="m-0 border-0 bg-blue-50 text-xs text-blue-800">
                            Lead
                          </Tag>
                        ) : item.kind === 'listing' ? (
                          <Tag className="m-0 border-0 bg-emerald-50 text-xs text-emerald-800">
                            Listing
                          </Tag>
                        ) : (
                          <Tag className="m-0 border-0 bg-amber-50 text-xs text-amber-900">
                            Review
                          </Tag>
                        );
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => navigate(item.href)}
                            className="group flex w-full cursor-pointer gap-3 rounded-xl px-2 py-3 text-left transition hover:bg-slate-50 sm:gap-4 sm:py-3.5"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                {tag}
                                <span className="text-xs text-slate-400">{dayjs(item.when).fromNow()}</span>
                              </div>
                              <p className="mt-1 line-clamp-2 text-sm font-medium text-slate-900 sm:text-base">
                                {item.title}
                              </p>
                              <p className="mt-0.5 line-clamp-2 text-xs text-slate-600 sm:text-sm">{item.meta}</p>
                            </div>
                            <ArrowRightOutlined className="mt-1 shrink-0 text-slate-300 group-hover:text-[#0171BF]" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-4 sm:gap-5 lg:col-span-4">
            <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <h2 className="text-base font-semibold text-slate-900">Quick actions</h2>
              <p className="mt-1 text-sm text-slate-500">Tap any row to go there</p>
              <div className="mt-4 flex flex-col gap-2">
                {quickActions.map((q) => (
                  <button
                    key={q.title}
                    type="button"
                    onClick={q.onClick}
                    className="group flex w-full cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left transition hover:border-[#0171BF] hover:bg-blue-50/50 sm:p-3.5"
                  >
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white shadow-md ${q.iconClass}`}
                    >
                      {q.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#0171BF] underline-offset-2 group-hover:underline">
                        {q.title}
                      </p>
                      <p className="text-xs text-slate-600 sm:text-sm">{q.description}</p>
                    </div>
                    <ArrowRightOutlined className="shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-[#0171BF]" />
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 p-4 sm:p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Last 30 days</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums text-slate-900 sm:text-3xl">
                {formatNum(stats?.leads.thisMonth ?? 0)}{' '}
                <span className="text-base font-normal text-slate-500 sm:text-lg">
                  {stats?.leads.thisMonth === 1 ? 'lead' : 'leads'}
                </span>
              </p>
              <p className="mt-2 text-sm text-slate-600">
                Answering quickly usually helps turn a message into a sale.
              </p>
              <Button
                type="default"
                className="mt-4 h-10 w-full cursor-pointer rounded-xl border-[#0171BF] font-medium text-[#0171BF] sm:w-auto sm:px-6"
                onClick={() => navigate('/leads')}
              >
                Open my leads
              </Button>
            </section>
          </aside>
        </div>
      </div>
    </PageShell>
  );
};

export default Dashboard;
